<script setup lang="ts">
    import { computed, ref } from 'vue';
    import QRCode from '@/views/plan/qr-code/QRCode.vue';
    import PrintableQRCode from '@/views/plan/qr-code/PrintableQRCode.vue';
    import type { ApiPlan, ApiPlansCase } from '@/views/plan/api/types';

    export interface Props {
        project: ApiPlansCase;
        plan: ApiPlan;
    }

    const props = defineProps<Props>();

    const showPrint = ref(false);

    async function onPrint(): Promise<void> {
        showPrint.value = true;

        setTimeout(() => {
            try {
                window.print();
            } finally {
                showPrint.value = false;
            }
        }, 500);
    }

    /**
     * The QR codes requires the report-measurements to be calculated.
     * It will be present before the plan is completed, but it takes around 1 minute to reach this state.
     */
    const isReady = computed((): boolean => {
        return props.plan.measurements !== null;
    });
</script>

<template>
    <v-card width="244" class="no-shadow pa-0 ma-0 border-none qr-code-container" variant="flat">
        <v-card-text class="text-center qr-code pa-0">
            <QRCode v-if="isReady" :size="115" :plan="plan" />
            <v-container
                v-else
                class="pa-5 mt-5 border-1-dashed text-grey-1 small-text plan-in-process-container d-block"
            >
                <v-progress-linear color="primary" class="mt-2" indeterminate height="6" />
                <br />
                Your QR Code is being created, this process can take up to 2 minutes.
            </v-container>
        </v-card-text>
        <v-card-title class="text-subtitle-2 d-inline-flex">
            <span class="text-secondary-text">Intellijoint Hip Plan QR Code:</span>
            <span v-if="isReady" class="ml-2 download-button text-primary" @click="onPrint">
                Print
            </span>
            <!--
                This is the QR code that the user will print
                This will include data as the case name, surgeon, patient + the qr code
            -->
            <teleport v-if="showPrint" to="#print-area">
                <PrintableQRCode ref="print" :project="project" :plan="plan" />
            </teleport>
        </v-card-title>
    </v-card>
</template>

<style scoped lang="scss">
    .qr-code-container {
        color: rgb(var(--v-theme-secondary));
    }

    .download-button {
        color: rgb(var(--v-theme-primary));
        text-transform: none;

        &:hover {
            cursor: pointer;
        }
    }
</style>
